import { useState, useEffect, useCallback } from 'react'
import { DashboardLayout } from '../../components/layout'
import { Button, StatCard, Modal } from '../../components/ui'
import { InvoiceList, InvoiceForm, ClientForm } from '../../features/invoices'
import { useBusinesses } from '../../hooks/useBusinesses'
import { useToast } from '../../context/ToastContext'
import { db } from '../../lib/supabase'
import { formatCurrency } from '../../lib/format'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

function InvoicesPage() {
  const { businesses } = useBusinesses()
  const toast = useToast()

  const [invoices, setInvoices] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)

  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [showClientModal, setShowClientModal] = useState(false)
  const [editInvoice, setEditInvoice] = useState(null)
  const [editClient, setEditClient] = useState(null)
  const [formLoading, setFormLoading] = useState(false)

  const [activeTab, setActiveTab] = useState('invoices') // 'invoices' or 'clients'

  const fetchData = useCallback(async () => {
    setLoading(true)
    const [invoicesResult, clientsResult] = await Promise.all([
      db.getInvoices(),
      db.getClients(),
    ])
    setInvoices(invoicesResult.data || [])
    setClients(clientsResult.data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Invoice handlers
  const handleInvoiceSubmit = async (formData) => {
    setFormLoading(true)

    if (editInvoice) {
      const { error } = await db.updateInvoice(editInvoice.id, formData)
      if (error) {
        toast.error('Failed to update invoice')
        setFormLoading(false)
        return
      }

      // Update line items
      // Delete existing items and add new ones
      for (const item of editInvoice.invoice_items || []) {
        await db.deleteInvoiceItem(item.id)
      }
      for (const item of formData.items) {
        await db.addInvoiceItem({
          invoice_id: editInvoice.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount,
        })
      }

      toast.success('Invoice updated')
    } else {
      const { data: invoice, error } = await db.createInvoice({
        business_id: formData.business_id,
        client_id: formData.client_id,
        due_date: formData.due_date,
        tax_rate: formData.tax_rate,
        notes: formData.notes,
        terms: formData.terms,
        subtotal: formData.subtotal,
        tax_amount: formData.tax_amount,
        total: formData.total,
      })

      if (error) {
        toast.error('Failed to create invoice')
        setFormLoading(false)
        return
      }

      // Add line items
      for (const item of formData.items) {
        await db.addInvoiceItem({
          invoice_id: invoice.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount,
        })
      }

      toast.success('Invoice created')
    }

    setFormLoading(false)
    setShowInvoiceModal(false)
    setEditInvoice(null)
    fetchData()
  }

  const handleSendInvoice = async (invoiceId) => {
    const { error } = await db.sendInvoice(invoiceId)
    if (error) {
      toast.error('Failed to send invoice')
      return
    }
    toast.success('Invoice marked as sent')
    fetchData()
  }

  const handleMarkPaid = async (invoiceId) => {
    const { error } = await db.markInvoicePaid(invoiceId)
    if (error) {
      toast.error('Failed to mark invoice as paid')
      return
    }
    toast.success('Invoice marked as paid')
    fetchData()
  }

  const handleDeleteInvoice = async (invoiceId) => {
    const { error } = await db.deleteInvoice(invoiceId)
    if (error) {
      toast.error('Failed to delete invoice')
      return
    }
    toast.success('Invoice deleted')
    fetchData()
  }

  // Client handlers
  const handleClientSubmit = async (formData) => {
    setFormLoading(true)
    const { error } = editClient
      ? await db.updateClient(editClient.id, formData)
      : await db.createClient(formData)
    setFormLoading(false)

    if (error) {
      toast.error(`Failed to ${editClient ? 'update' : 'add'} client`)
      return
    }

    toast.success(`Client ${editClient ? 'updated' : 'added'}`)
    setShowClientModal(false)
    setEditClient(null)
    fetchData()
  }

  const handleDeleteClient = async (clientId) => {
    const { error } = await db.deleteClient(clientId)
    if (error) {
      toast.error('Failed to delete client. They may have existing invoices.')
      return
    }
    toast.success('Client deleted')
    fetchData()
  }

  if (loading) {
    return (
      <DashboardLayout title="Invoices">
        <LoadingSpinner.Overlay message="Loading invoices..." />
      </DashboardLayout>
    )
  }

  // Calculate stats
  const totalOutstanding = invoices
    .filter(i => ['sent'].includes(i.status))
    .reduce((sum, i) => sum + parseFloat(i.total), 0)

  const totalPaid = invoices
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + parseFloat(i.amount_paid || i.total), 0)

  const overdueInvoices = invoices.filter(i =>
    i.status === 'sent' && new Date(i.due_date) < new Date()
  )

  const totalOverdue = overdueInvoices.reduce((sum, i) => sum + parseFloat(i.total), 0)

  return (
    <DashboardLayout
      title="Invoices"
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => { setEditClient(null); setShowClientModal(true) }}>
            + Add Client
          </Button>
          <Button onClick={() => { setEditInvoice(null); setShowInvoiceModal(true) }}>
            + Create Invoice
          </Button>
        </div>
      }
    >
      {/* Stats */}
      <div className="grid grid-4 mb-8">
        <StatCard
          label="Outstanding"
          value={formatCurrency(totalOutstanding)}
        />
        <StatCard
          label="Overdue"
          value={formatCurrency(totalOverdue)}
          style={totalOverdue > 0 ? { borderColor: 'var(--error)' } : {}}
        />
        <StatCard
          label="Paid (All Time)"
          value={formatCurrency(totalPaid)}
        />
        <StatCard
          label="Total Clients"
          value={clients.length}
        />
      </div>

      {/* Tabs */}
      <div className="tabs mb-6">
        <button
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices ({invoices.length})
        </button>
        <button
          className={`tab ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => setActiveTab('clients')}
        >
          Clients ({clients.length})
        </button>
      </div>

      {/* Content */}
      {activeTab === 'invoices' ? (
        <InvoiceList
          invoices={invoices}
          onView={(invoice) => {
            setEditInvoice(invoice)
            setShowInvoiceModal(true)
          }}
          onEdit={(invoice) => {
            setEditInvoice(invoice)
            setShowInvoiceModal(true)
          }}
          onDelete={handleDeleteInvoice}
          onSend={handleSendInvoice}
          onMarkPaid={handleMarkPaid}
        />
      ) : (
        <div className="clients-list">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Email</th>
                <th>Business</th>
                <th>Invoices</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(client => {
                const clientInvoices = invoices.filter(i => i.client_id === client.id)
                return (
                  <tr key={client.id}>
                    <td className="font-medium">{client.name}</td>
                    <td>{client.company || '-'}</td>
                    <td>{client.email}</td>
                    <td>
                      {client.businesses && (
                        <span>{client.businesses.emoji} {client.businesses.name}</span>
                      )}
                    </td>
                    <td>{clientInvoices.length}</td>
                    <td>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setEditClient(client)
                            setShowClientModal(true)
                          }}
                        >
                          Edit
                        </Button>
                        {clientInvoices.length === 0 && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeleteClient(client.id)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
              {clients.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-8">
                    No clients yet. Add your first client to start invoicing.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoice Modal */}
      <Modal
        isOpen={showInvoiceModal}
        onClose={() => { setShowInvoiceModal(false); setEditInvoice(null) }}
        title={editInvoice ? 'Edit Invoice' : 'Create Invoice'}
        size="lg"
      >
        <InvoiceForm
          initialData={editInvoice}
          businesses={businesses}
          clients={clients}
          onSubmit={handleInvoiceSubmit}
          onCancel={() => { setShowInvoiceModal(false); setEditInvoice(null) }}
          loading={formLoading}
        />
      </Modal>

      {/* Client Modal */}
      <Modal
        isOpen={showClientModal}
        onClose={() => { setShowClientModal(false); setEditClient(null) }}
        title={editClient ? 'Edit Client' : 'Add Client'}
      >
        <ClientForm
          initialData={editClient}
          businesses={businesses}
          onSubmit={handleClientSubmit}
          onCancel={() => { setShowClientModal(false); setEditClient(null) }}
          loading={formLoading}
        />
      </Modal>
    </DashboardLayout>
  )
}

export default InvoicesPage
